"use client";

import { AnimatePresence, motion, useInView } from "framer-motion";
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronDown,
  type LucideIcon,
  Minus,
  Radar,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { cn, formatNumber } from "@/lib/utils";
import { EASE } from "../../constants";
import { DotBadge, Panel, type Stat, StatStrip } from "../../primitives";
import { SCENE_SPEEDUP, scaleDelay } from "../../timing";
import type { SceneProps } from "../../types";
import { useSceneFinish } from "../../use-scene-finish";
import { useStickToBottom } from "../../use-stick-to-bottom";
import {
  INSIGHT,
  type LocationRow,
  type LocationStatus,
  LOCATIONS,
  PIPELINE,
  type Stage,
} from "./data";

const fmtNum = (v: number) => formatNumber(v, { precision: 0 });

const STAGE_BADGE: Record<Stage, { label: string; dot: string }> = {
  presale: { label: "Pre-sale", dot: "bg-blue-500" },
  opening: { label: "Opening", dot: "bg-primary" },
  active: { label: "Active", dot: "bg-success" },
};

const STATUS_BADGE: Record<LocationStatus, { label: string; dot: string | null }> = {
  healthy: { label: "Healthy", dot: "bg-success" },
  ontrack: { label: "On track", dot: "bg-blue-500" },
  prepping: { label: "Prepping", dot: "bg-muted-foreground/40" },
  risk: { label: "Needs attention", dot: "bg-amber-500" },
};

function Trend({ value }: { value: number }) {
  if (value === 0)
    return (
      <span className="inline-flex items-center justify-end gap-0.5 text-xs tabular-nums text-muted-foreground">
        <Minus className="h-3 w-3" />
      </span>
    );
  const up = value > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-end gap-0.5 text-xs font-medium tabular-nums",
        up ? "text-success" : "text-amber-600 dark:text-amber-400",
      )}
    >
      <Icon className="h-3 w-3" />
      {up ? "+" : "−"}
      {Math.abs(value)}%
    </span>
  );
}

function PipelineStage({
  icon: Icon,
  label,
  sub,
  count,
  last,
  index,
  reduce,
}: {
  icon: LucideIcon;
  label: string;
  sub: string;
  count: number;
  last?: boolean;
  index: number;
  reduce: boolean;
}) {
  return (
    <motion.div
      className="relative"
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE, delay: reduce ? 0 : 0.2 + index * 0.55 }}
    >
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-background px-2.5 py-1.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50">
          <Icon className="h-3.5 w-3.5 text-foreground" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-foreground">{label}</p>
          <p className="truncate text-[11px] text-muted-foreground">{sub}</p>
        </div>
        <span className="text-base font-bold tabular-nums text-foreground">{count}</span>
      </div>
      {!last ? (
        <div className="-my-0.5 flex justify-center">
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/60" />
        </div>
      ) : null}
    </motion.div>
  );
}

function LocationRowItem({
  row,
  flagged,
  reduce,
}: {
  row: LocationRow;
  flagged: boolean;
  reduce: boolean;
}) {
  const highlighted = row.risk && flagged;
  // The slipping row reads a neutral "Active" until corporate is alerted, then
  // flips to "Needs attention" — that flip is the moment the insight surfaces.
  const badge =
    row.risk && !flagged ? { label: "Active", dot: "bg-success" } : STATUS_BADGE[row.status];
  return (
    <motion.div
      layout
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2.5 rounded-lg border px-2.5 py-2 transition-colors sm:grid-cols-[1.5fr_0.7fr_0.6fr_0.9fr] sm:px-3",
        highlighted ? "border-border bg-muted/50" : "border-transparent",
      )}
    >
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium text-foreground">{row.name}</p>
        <p className="truncate text-[11px] text-muted-foreground">{row.metric}</p>
      </div>
      <span className="hidden sm:block">
        <DotBadge dot={STAGE_BADGE[row.stage].dot}>{STAGE_BADGE[row.stage].label}</DotBadge>
      </span>
      <Trend value={row.trend} />
      <span className="flex justify-end">
        <DotBadge dot={badge.dot}>{badge.label}</DotBadge>
      </span>
    </motion.div>
  );
}

// Network-wide trend lines shown before any location is flagged — real data so
// the monitor reads as a live dashboard, not dead whitespace.
const SIGNALS: { label: string; value: string; data: number[] }[] = [
  { label: "Visits", value: "+6%", data: [5, 6, 5, 7, 6, 8, 7, 9] },
  { label: "Pre-sales", value: "+12%", data: [3, 4, 4, 5, 6, 7, 8, 10] },
  { label: "Retention", value: "94%", data: [8, 9, 8, 9, 9, 8, 9, 9] },
];

function Sparkline({ data, reduce }: { data: number[]; reduce: boolean }) {
  const w = 100;
  const h = 22;
  const pad = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (d - min) / range) * (h - pad * 2);
    return [x, y] as const;
  });
  const line = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const area = `${pts[0][0]},${h} ${line} ${pts[pts.length - 1][0]},${h}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="h-5 flex-1"
      aria-hidden="true"
    >
      <polygon points={area} className="fill-primary/10 dark:fill-brand-accent/10" />
      <motion.polyline
        points={line}
        fill="none"
        className="stroke-primary dark:stroke-brand-accent"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        initial={reduce ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, ease: EASE }}
      />
    </svg>
  );
}

function SignalRow({
  label,
  value,
  data,
  reduce,
}: {
  label: string;
  value: string;
  data: number[];
  reduce: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-[68px] shrink-0 text-[11px] text-muted-foreground">{label}</span>
      <Sparkline data={data} reduce={reduce} />
      <span className="w-10 shrink-0 text-right text-[11px] font-medium tabular-nums text-success">
        {value}
      </span>
      <span className="size-1.5 shrink-0 rounded-full bg-success" />
    </div>
  );
}

function MonitorCard({ flagged, reduce }: { flagged: boolean; reduce: boolean }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      {flagged ? (
        <motion.div
          key="alert"
          initial={reduce ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? undefined : { opacity: 0, y: -8 }}
          transition={{ duration: 0.3, ease: EASE }}
          className="flex w-full flex-col rounded-md border border-border bg-background p-3"
        >
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="text-[13px] font-semibold text-foreground">Needs attention</span>
          </div>
          <p className="mt-2 text-[13px] font-medium text-foreground">{INSIGHT.location}</p>
          <p className="text-[12px] text-muted-foreground">{INSIGHT.headline}</p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {INSIGHT.signals.map((s) => (
              <span
                key={s.label}
                className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {s.label} <span className="font-semibold text-foreground">{s.value}</span>
              </span>
            ))}
          </div>
          <button
            type="button"
            className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Review location
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      ) : (
        <motion.div
          key="monitor"
          initial={false}
          animate={{ opacity: 1 }}
          exit={reduce ? undefined : { opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          className="flex w-full flex-col rounded-md border border-border bg-background p-3"
        >
          <div className="flex items-center gap-1.5">
            <Radar className="h-3.5 w-3.5 shrink-0 animate-pulse text-primary dark:text-brand-accent" />
            <span className="text-[13px] font-medium text-foreground">Live monitoring</span>
            <DotBadge dot="bg-success" className="ml-auto">
              On track
            </DotBadge>
          </div>
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
            Helios watches every location across the network and flags any that start to slip.
          </p>
          <div className="mt-3 flex flex-col gap-2">
            {SIGNALS.map((s) => (
              <SignalRow
                key={s.label}
                label={s.label}
                value={s.value}
                data={s.data}
                reduce={reduce}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function GrowLocations({ reduce, paused = false, onComplete }: SceneProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.3 });
  const [revealed, setRevealed] = useState(0);
  const [flagged, setFlagged] = useState(false);

  const total = LOCATIONS.length;

  // Stream the location rows in while in view. The first row waits for the
  // pipeline funnel to finish building, then rows reveal slowly enough to read.
  useEffect(() => {
    if (reduce || paused || !inView || revealed >= total) return;
    const id = setTimeout(
      () => setRevealed((v) => v + 1),
      scaleDelay(revealed === 0 ? 2000 : 1100, SCENE_SPEEDUP),
    );
    return () => clearTimeout(id);
  }, [inView, revealed, reduce, paused, total]);

  // Once every location is on screen, surface the at-risk insight.
  useEffect(() => {
    if (reduce || paused || !inView || revealed < total || flagged) return;
    const id = setTimeout(() => setFlagged(true), scaleDelay(1600, SCENE_SPEEDUP));
    return () => clearTimeout(id);
  }, [inView, revealed, flagged, reduce, paused, total]);

  const effRevealed = reduce ? total : revealed;
  const effFlagged = reduce ? true : flagged;

  // Keep the latest location in view as rows stream in — on mobile the list is
  // short and new rows reveal below the fold, which loses the effect. Backs off
  // if the user scrolls up, resumes when they return to the bottom.
  useStickToBottom(listRef, effRevealed, reduce);

  // Final tab — once the insight surfaces and holds, loop back to the first.
  useSceneFinish({
    active: effFlagged && inView && !paused,
    delay: scaleDelay(3600, SCENE_SPEEDUP),
    enabled: !reduce,
    onComplete,
  });

  const shown = LOCATIONS.slice(0, effRevealed);
  const needsAttention = effFlagged ? 1 : 0;

  const stats: Stat[] = [
    { name: "Members", value: 8420, formatter: fmtNum },
    { name: "Locations", value: 24, formatter: fmtNum },
    // { name: "Network MRR", value: 214, formatter: (v) => `$${fmtNum(v)}K` },
    {
      name: "Attention",

      value: needsAttention,
      formatter: fmtNum,
      numberClassName: needsAttention > 0 ? "text-amber-600 dark:text-amber-400" : undefined,
    },
  ];

  return (
    <div ref={rootRef} className="flex h-[36rem] flex-col p-4 sm:p-5 lg:h-full">
      {/* Top level: the playbook + network rollup stats */}
      <div className="flex flex-col gap-4 border-b border-border pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Visibility across every location.
          </h3>
          <p className="text-sm text-muted-foreground">
            Pre-sale → Opened → Active. Run every location like your best one.
          </p>
        </div>
        <StatStrip stats={stats} />
      </div>

      {/* Pipeline + monitoring on the left, the live location table on the right.
          On mobile the pipeline drops away and the monitor sits above the table. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 pt-4 lg:grid lg:grid-cols-[300px_1fr] lg:gap-5">
        <div className="flex shrink-0 flex-col gap-2 lg:min-h-0">
          <div className="hidden rounded-md border border-border bg-background p-2.5 lg:block">
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className="text-[13px] font-medium text-foreground">Growth pipeline</span>
              <Badge variant="outline" className="ml-auto bg-background text-[10px]">
                24 locations
              </Badge>
            </div>
            {PIPELINE.map((stage, i) => (
              <PipelineStage
                key={stage.id}
                icon={stage.icon}
                label={stage.label}
                sub={stage.sub}
                count={stage.count}
                last={i === PIPELINE.length - 1}
                index={i}
                reduce={reduce}
              />
            ))}
          </div>
          <MonitorCard flagged={effFlagged} reduce={reduce} />
        </div>

        <Panel className="min-h-0 flex-1 overflow-hidden">
          <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2.5 border-b border-border px-2.5 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid-cols-[1.5fr_0.7fr_0.6fr_0.9fr] sm:px-3">
            <span>Location</span>
            <span className="hidden sm:block">Stage</span>
            <span className="text-right">30-day</span>
            <span className="text-right">Status</span>
          </div>
          <div
            ref={listRef}
            className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <AnimatePresence initial={false}>
              {shown.map((row) => (
                <LocationRowItem key={row.id} row={row} flagged={effFlagged} reduce={reduce} />
              ))}
            </AnimatePresence>
          </div>
        </Panel>
      </div>
    </div>
  );
}
